/**
 * CopilotPanel — AI marketing specialist chat panel.
 *
 * Renders as a side panel within the Marketing page. Supports:
 * - Text chat with SSE streaming
 * - Inline visualizations (rendered between messages)
 * - Action proposals with approve/dismiss
 * - Per-message cost tracking
 * - Session usage footer
 * - Language toggle (EN/ES)
 * - Briefing card on first open
 *
 * Composed from the app's canonical chat primitives: ChatBubble family for
 * messages, ChatInput for the composer (including its built-in STT path),
 * DropdownMenu for conversation history.
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import {
  Card,
  CardContent,
  Badge,
  Button,
  ChatBubble,
  ChatBubbleAvatar,
  ChatBubbleContent,
  ChatBubbleMessage,
  ChatBubbleMeta,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui'
import {
  X,
  Plus,
  CheckCircle,
  XCircle,
  Sparkles,
  Globe,
  Info,
  History,
  Trash2,
  User,
  Volume2,
  VolumeX,
} from 'lucide-react'
import { useMarketingCopilot } from '@/hooks/useMarketingCopilot'
import { useCopilotVoice } from '@/hooks/useCopilotVoice'
import { useCopilotTTS } from '@/hooks/useCopilotTTS'
import { useSettingsStore } from '@/stores'
import { useMarketingT } from '@/i18n/marketing'
import type { CopilotMessage, ActionProposal, CopilotPersona } from '@/types/copilot'
import { RenderedMessage } from '@/components/chat/RenderedMessage'
import { ChatInput } from '@/components/chat/ChatInput'
import { VisualizationRenderer } from '@/components/marketing/VisualizationRenderer'
import type { VizInteractionEvent } from '@/components/marketing/VisualizationRenderer'

const SUGGESTED_PROMPTS_EN = [
  'How are my campaigns performing this week?',
  'Which campaign has the best ROAS?',
  'Show me the attribution funnel',
  'What optimizations do you recommend?',
  'Compare this week to last week',
]

const SUGGESTED_PROMPTS_ES = [
  '¿Cómo van mis campañas esta semana?',
  '¿Qué campaña tiene el mejor ROAS?',
  'Muéstrame el embudo de atribución',
  '¿Qué optimizaciones recomiendas?',
  'Compara esta semana con la anterior',
]

export function CopilotPanel() {
  const t = useMarketingT()
  // Persisted, default-off, so nobody gets unsolicited audio and a mute survives a reload.
  const speechEnabled = useSettingsStore((s) => s.ui.copilotAutoSpeak)
  const setSpeechEnabled = useSettingsStore((s) => s.setCopilotAutoSpeak)

  // The copilot hook needs TTS callbacks and the TTS hook needs the persona
  // the copilot hook returns — a ref breaks the cycle. Callbacks only fire
  // mid-stream, long after both hooks have run.
  const ttsRef = useRef<ReturnType<typeof useCopilotTTS> | null>(null)

  const {
    sendMessage,
    messages,
    isStreaming,
    isOpen,
    togglePanel,
    language,
    setLanguage,
    sessionUsage,
    briefing,
    briefingAnomalies,
    loadBriefing,
    approveAction,
    dismissAction,
    newConversation,
    conversations,
    loadConversations,
    selectConversation,
    deleteConversation,
    conversationId,
    persona,
    loadPersona,
  } = useMarketingCopilot({
    onAssistantToken: (token) => ttsRef.current?.processToken(token),
    onAssistantComplete: () => ttsRef.current?.flush(),
    // Don't leave a half-spoken sentence hanging on a failed stream.
    onAssistantError: () => ttsRef.current?.interrupt(),
  })

  const tts = useCopilotTTS({ persona: persona ?? null, enabled: speechEnabled })
  useEffect(() => {
    ttsRef.current = tts
  })

  const displayName = persona?.name || t('copilot.title')

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const [briefingLoaded, setBriefingLoaded] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  // Final voice transcript, fed to ChatInput's voiceTranscript path so the
  // user can review/edit the spoken text before sending.
  const [voiceText, setVoiceText] = useState('')

  // Voice input (shared push-to-talk STT engine; UI is ChatInput's built-in mic)
  const {
    isListening,
    partialTranscript,
    startListening,
    stopListening,
    isAvailable: voiceAvailable,
    error: voiceError,
  } = useCopilotVoice({
    onTranscript: (text) => setVoiceText(text),
    language,
  })

  const handleToggleListening = useCallback(() => {
    if (isListening) {
      stopListening()
    } else {
      setVoiceText('')
      void startListening()
    }
  }, [isListening, startListening, stopListening])

  // Every send path funnels through here so audio is always primed from the
  // user gesture — `prepareForNewStream` creates the AudioContext, which
  // browsers only allow during a gesture (same rule ChatContainer follows).
  const startTurn = useCallback(
    async (text: string) => {
      await ttsRef.current?.prepare()
      sendMessage(text)
    },
    [sendMessage],
  )

  // Clear the voice draft once sent — ChatInput seeds its textarea from
  // voiceTranscript on mount, so a stale value would resurrect the sent
  // text when the panel is closed and reopened.
  const handleSend = useCallback(
    (text: string) => {
      setVoiceText('')
      void startTurn(text)
    },
    [startTurn],
  )

  const handleToggleSpeech = useCallback(() => {
    if (speechEnabled) ttsRef.current?.interrupt()
    setSpeechEnabled(!speechEnabled)
  }, [speechEnabled, setSpeechEnabled])

  const handleNewConversation = useCallback(() => {
    ttsRef.current?.interrupt()
    newConversation()
  }, [newConversation])

  // The panel's hooks stay mounted behind the FAB, so closing has to drop
  // the ElevenLabs socket explicitly — nothing else ever would.
  const handleClose = useCallback(() => {
    void ttsRef.current?.stop()
    togglePanel()
  }, [togglePanel])

  const handleSelectConversation = useCallback(
    (id: string) => {
      ttsRef.current?.interrupt()
      void selectConversation(id)
    },
    [selectConversation],
  )

  // Voice drafts die with the panel, like typed drafts do (ChatInput
  // unmounts when the panel closes).
  useEffect(() => {
    if (!isOpen) setVoiceText('')
  }, [isOpen])

  const handleHistoryOpenChange = useCallback(
    async (open: boolean) => {
      if (open) {
        // Load before opening so the menu never flashes "No conversations yet"
        await loadConversations()
      }
      setHistoryOpen(open)
    },
    [loadConversations],
  )

  const handleDeleteConversation = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.stopPropagation()
      void deleteConversation(id)
    },
    [deleteConversation],
  )

  // Keyboard route to the trash button, which Tab can't reach inside a Radix
  // menu (arrow keys move between items, Tab closes the menu) — PR 6 review
  // chip. Delete only: Backspace is too easy to hit next to a menu with
  // typeahead, and there is no confirmation step.
  const handleDeleteKeyDown = useCallback(
    (e: React.KeyboardEvent, id: string) => {
      if (e.key !== 'Delete') return
      e.preventDefault()
      e.stopPropagation()
      void deleteConversation(id)
    },
    [deleteConversation],
  )

  // Load briefing + configured identity on first open
  useEffect(() => {
    if (isOpen && !briefingLoaded) {
      loadBriefing()
      void loadPersona?.()
      setBriefingLoaded(true)
    }
  }, [isOpen, briefingLoaded, loadBriefing, loadPersona])

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSuggestedPrompt = useCallback((prompt: string) => {
    void startTurn(prompt)
  }, [startTurn])

  const handleVizDrillDown = useCallback((event: VizInteractionEvent) => {
    if (isStreaming) return
    const drillPrompt = language === 'es'
      ? `Cuéntame más sobre "${event.label}" del ${event.vizType === 'funnel' ? 'embudo' : 'gráfico'}.`
      : `Tell me more about "${event.label}" from the ${event.vizType}.`
    void startTurn(drillPrompt)
  }, [isStreaming, language, startTurn])

  const suggestedPrompts = language === 'es' ? SUGGESTED_PROMPTS_ES : SUGGESTED_PROMPTS_EN

  if (!isOpen) {
    return (
      <Button
        onClick={togglePanel}
        className="fixed bottom-6 right-6 z-50 size-14 rounded-full p-0 shadow-lg transition-all hover:scale-105"
        title="Open Marketing Copilot"
      >
        <Sparkles className="size-6" />
      </Button>
    )
  }

  return (
    <div className="flex flex-col h-full border-l border-border bg-background">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-linear-to-r from-primary/10 to-primary/5">
        <div className="flex items-center gap-2 min-w-0">
          <CopilotGlyph persona={persona} label={displayName} className="size-5 shrink-0" />
          <span className="font-semibold text-sm truncate">
            {displayName}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {/* Speaker toggle — only when spoken output is actually possible */}
          {tts.available && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleToggleSpeech}
              aria-label={speechEnabled ? 'Mute spoken replies' : 'Unmute spoken replies'}
              aria-pressed={speechEnabled}
              title={speechEnabled ? 'Mute spoken replies' : 'Unmute spoken replies'}
            >
              {speechEnabled ? (
                <Volume2 className={`w-4 h-4 ${tts.isSpeaking ? 'text-primary' : ''}`} />
              ) : (
                <VolumeX className="w-4 h-4" />
              )}
            </Button>
          )}
          {/* Language toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLanguage(language === 'en' ? 'es' : 'en')}
            title="Switch language"
            className="text-xs px-2"
          >
            <Globe className="w-3 h-3 mr-1" />
            {language.toUpperCase()}
          </Button>
          {/* Conversation history */}
          <DropdownMenu open={historyOpen} onOpenChange={handleHistoryOpenChange}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" title="Conversation history">
                <History className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72 p-0">
              <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                <span className="text-xs font-semibold text-muted-foreground">Recent conversations</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setHistoryOpen(false); handleNewConversation() }}
                  title="New conversation"
                  className="h-6 w-6 p-0"
                >
                  <Plus className="w-3.5 h-3.5" />
                </Button>
              </div>
              <div className="max-h-64 overflow-y-auto">
                {conversations.length === 0 ? (
                  <div className="px-3 py-4 text-xs text-muted-foreground text-center">
                    No conversations yet
                  </div>
                ) : (
                  conversations.map((conv) => (
                    <DropdownMenuItem
                      key={conv.id}
                      onSelect={() => handleSelectConversation(conv.id)}
                      onKeyDown={(e) => handleDeleteKeyDown(e, conv.id)}
                      className={`group cursor-pointer gap-2 rounded-none px-3 py-2 ${
                        conv.id === conversationId ? 'bg-primary/10' : ''
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate text-popover-foreground">
                          {conv.title}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {conv.messageCount} message{conv.messageCount !== 1 ? 's' : ''}
                          {' · '}
                          {new Date(conv.updatedAt).toLocaleDateString()}
                        </p>
                      </div>
                      {/* Mouse route to delete; the keyboard route is the
                          Delete/Backspace handler on the row, since Tab
                          can't reach this button inside a Radix menu. */}
                      <button
                        type="button"
                        onClick={(e) => handleDeleteConversation(e, conv.id)}
                        className="shrink-0 p-1 rounded-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
                        title="Delete conversation"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </DropdownMenuItem>
                  ))
                )}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
          {/* New conversation */}
          <Button variant="ghost" size="sm" onClick={handleNewConversation} title="New conversation">
            <Plus className="w-4 h-4" />
          </Button>
          {/* Close */}
          <Button variant="ghost" size="sm" onClick={handleClose} title="Close">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {/* Briefing card (shown when no messages) */}
        {messages.length === 0 && briefing && (
          <Card className="bg-linear-to-br from-primary/10 to-primary/5 border-primary/20">
            <CardContent className="pt-4 pb-3 px-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm font-semibold">
                  {t('copilot.briefing.title')}
                </span>
              </div>
              <div className="text-xs text-foreground leading-relaxed">
                <RenderedMessage content={briefing} />
              </div>
              {briefingAnomalies.length > 0 && (
                <div className="mt-3 pt-2 border-t border-primary/20">
                  <p className="text-xs font-semibold text-warning-strong mb-1.5">
                    {briefingAnomalies.length} Alert{briefingAnomalies.length !== 1 ? 's' : ''} Detected
                  </p>
                  <div className="space-y-1">
                    {briefingAnomalies.slice(0, 3).map((a, i) => (
                      <div
                        key={i}
                        className={`text-xs px-2 py-1 rounded ${
                          a.sentiment === 'positive'
                            ? 'bg-success/10 text-success'
                            : 'bg-destructive/10 text-destructive'
                        }`}
                      >
                        <span className="font-medium">{a.campaign}</span>
                        {': '}
                        {a.metric.replace(/_/g, ' ')} {a.direction} {Math.abs(a.pct_change)}%
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Suggested prompts (shown when no messages) */}
        {messages.length === 0 && (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              {t('copilot.tryAsking')}
            </p>
            <div className="flex flex-wrap gap-2">
              {suggestedPrompts.map((prompt) => (
                <Button
                  key={prompt}
                  variant="outline"
                  size="sm"
                  onClick={() => handleSuggestedPrompt(prompt)}
                  className="h-auto whitespace-normal rounded-full px-3 py-1.5 text-xs font-normal"
                >
                  {prompt}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Message bubbles */}
        {messages.map((msg) => (
          <CopilotMessageBubble
            key={msg.id}
            message={msg}
            persona={persona}
            personaLabel={displayName}
            onApproveAction={approveAction}
            onDismissAction={dismissAction}
            onDrillDown={handleVizDrillDown}
          />
        ))}

        <div ref={messagesEndRef} />
      </div>

      {/* Session usage footer */}
      {sessionUsage.requestCount > 0 && (
        <div className="px-4 py-1 text-xs text-muted-foreground border-t border-border flex justify-between">
          <span>
            Session: {sessionUsage.totalTokens.toLocaleString()} tokens
            {' · '}${sessionUsage.totalCostUsd.toFixed(4)}
          </span>
          <span>{sessionUsage.requestCount} request{sessionUsage.requestCount !== 1 ? 's' : ''}</span>
        </div>
      )}

      {/* Input area */}
      {tts.error && (
        <div className="px-4 py-1 text-xs text-warning-strong bg-warning/10">
          Spoken replies stopped: {tts.error}
        </div>
      )}
      {voiceError && (
        <div className="px-4 py-1 text-xs text-destructive bg-destructive/10">
          {voiceError}
        </div>
      )}
      <ChatInput
        onSend={handleSend}
        disabled={isStreaming}
        placeholder={isListening ? 'Listening...' : t('copilot.placeholder')}
        sttEnabled={voiceAvailable}
        isListening={isListening}
        voiceTranscript={isListening && partialTranscript ? partialTranscript : voiceText}
        onToggleListening={handleToggleListening}
      />
    </div>
  )
}


// ── Persona glyph ───────────────────────────────────────────────────────

/**
 * The copilot's face: the configured avatar when there is one, the Sparkles
 * glyph otherwise. The name doubles as the image's accessible label so an
 * avatar without a configured name stays on the glyph rather than shipping
 * an unlabelled image.
 */
function CopilotGlyph({
  persona,
  label,
  className = '',
  imageClassName,
}: {
  persona?: CopilotPersona | null
  /** Accessible name for the avatar image (the copilot's display name). */
  label: string
  className?: string
  /** Sizing for the avatar image, when it differs from the glyph's. */
  imageClassName?: string
}) {
  // A dead CDN would otherwise leave a broken-image icon in the header and on
  // every assistant bubble; fall back to the glyph the first time it fails.
  const [broken, setBroken] = useState(false)
  const url = persona?.avatarUrl

  if (url && !broken) {
    return (
      <img
        src={url}
        alt={label}
        // Don't leak the app's URL to whatever host serves the avatar.
        referrerPolicy="no-referrer"
        onError={() => setBroken(true)}
        className={`rounded-full object-cover ${imageClassName ?? className}`}
      />
    )
  }
  return <Sparkles className={`text-primary ${className}`} />
}


// ── Message bubble component ────────────────────────────────────────────

function CopilotMessageBubble({
  message,
  persona,
  personaLabel,
  onApproveAction,
  onDismissAction,
  onDrillDown,
}: {
  message: CopilotMessage
  persona?: CopilotPersona | null
  personaLabel: string
  onApproveAction: (id: string) => void
  onDismissAction: (id: string) => void
  onDrillDown?: (event: VizInteractionEvent) => void
}) {
  const isUser = message.role === 'user'

  return (
    <ChatBubble variant={isUser ? 'sent' : 'received'}>
      {!isUser && (
        <ChatBubbleAvatar tone="primary" className="overflow-hidden">
          <CopilotGlyph
            persona={persona}
            label={personaLabel}
            className="size-4"
            imageClassName="size-full"
          />
        </ChatBubbleAvatar>
      )}

      <ChatBubbleContent>
        <ChatBubbleMessage loading={!message.content && !!message.isStreaming}>
          {message.content ? <RenderedMessage content={message.content} /> : null}
        </ChatBubbleMessage>

        {/* Inline visualizations */}
        {message.visualizations && message.visualizations.length > 0 && (
          <div className="mt-1 space-y-2">
            {message.visualizations.map((viz) => (
              <Card key={viz.id} className="overflow-hidden">
                <CardContent className="p-3">
                  <p className="text-xs font-medium mb-1">{viz.title}</p>
                  {viz.description && (
                    <p className="text-xs text-muted-foreground mb-2">{viz.description}</p>
                  )}
                  <VisualizationRenderer viz={viz} onDrillDown={onDrillDown} />
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Action proposals */}
        {message.actionProposals && message.actionProposals.length > 0 && (
          <div className="mt-1 space-y-2">
            {message.actionProposals.map((action) => (
              <ActionProposalCard
                key={action.id}
                action={action}
                onApprove={() => onApproveAction(action.id)}
                onDismiss={() => onDismissAction(action.id)}
              />
            ))}
          </div>
        )}

        {/* Usage info (assistant messages only) */}
        {!isUser && message.usage && !message.isStreaming && (
          <ChatBubbleMeta>
            <Info className="w-3 h-3 shrink-0" />
            <span className="truncate">
              {message.usage.model} · {message.usage.totalTokens.toLocaleString()} tokens · ${message.usage.costUsd.total.toFixed(4)}
            </span>
          </ChatBubbleMeta>
        )}
      </ChatBubbleContent>

      {isUser && (
        <ChatBubbleAvatar>
          <User className="w-4 h-4" />
        </ChatBubbleAvatar>
      )}
    </ChatBubble>
  )
}


// ── Action proposal card ────────────────────────────────────────────────

function ActionProposalCard({
  action,
  onApprove,
  onDismiss,
}: {
  action: ActionProposal
  onApprove: () => void
  onDismiss: () => void
}) {
  const confidenceColor = {
    high: 'bg-success/10 text-success',
    medium: 'bg-warning/10 text-warning-strong',
    low: 'bg-destructive/10 text-destructive',
  }

  return (
    <Card className="border-primary/20">
      <CardContent className="p-3 space-y-2">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium">{action.description}</p>
            {action.impactEstimate && (
              <p className="text-xs text-muted-foreground mt-1">{action.impactEstimate}</p>
            )}
          </div>
          <Badge className={`text-xs ml-2 ${confidenceColor[action.confidence]}`}>
            {action.confidence}
          </Badge>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={onDismiss}>
            <XCircle className="w-3 h-3 mr-1" />
            Dismiss
          </Button>
          <Button size="sm" onClick={onApprove}>
            <CheckCircle className="w-3 h-3 mr-1" />
            Approve
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
